import { Diagnostic, StatusData } from "@fairfleet/geotab";
import { normalizeUnitOfMeasure } from "../utilities/checklistValues";
import { LiveDataRow } from "./LiveDataRow";

/**
 * The unit a diagnostic is shown in for this user, as their measurement profile defines it.
 *
 * Geotab reports every reading in a base unit and leaves the conversion to the profile, so the
 * factor and offset have to travel with the unit name — the name alone would label a value in
 * metres as kilometres (an odometer of 25.348 km read as 25.348.700 km).
 */
export interface DisplayUnit {
  name: string;
  factorFromBase?: number;
  offsetFromBase?: number;
}

/** Everything that has to be looked up per reading, indexed by diagnostic id. */
export interface LiveDataLookups {
  /** Diagnostics as far as they are known yet; they arrive from a separate query. */
  diagnostics: Record<string, Diagnostic>;
  units: Record<string, DisplayUnit>;
  /**
   * The readable source name per diagnostic.
   *
   * Precomputed, because a source is a property of the diagnostic rather than of the reading:
   * derived per reading it was the same dozen values parsed tens of thousands of times.
   */
  sources: Record<string, string>;
}

/**
 * The unit a diagnostic is shown in: the user's measurement profile first, then the diagnostic's
 * own unit. Empty where neither is known.
 *
 * One place, so a page that summarises readings instead of listing them describes a reading's
 * unit the same way the live data page does.
 */
export function unitFor(diagnosticId: string, lookups: LiveDataLookups): string {
  const diagnostic = diagnosticId ? lookups.diagnostics[diagnosticId] : undefined;
  const unit = diagnosticId ? lookups.units[diagnosticId] : undefined;

  return unit?.name || normalizeUnitOfMeasure(diagnostic?.unitOfMeasure?.id);
}

/**
 * Turns one raw `StatusData` reading into a displayable row.
 *
 * Shared because the diagnostic name, its unit and its source all come from separate queries
 * that arrive later than the reading itself — every page showing readings has to combine them
 * the same way, or the same reading appears differently on two pages.
 *
 * @param statusData - The reading.
 * @param lookups - See {@link LiveDataLookups}.
 */
export function toLiveDataRow(statusData: StatusData, lookups: LiveDataLookups): LiveDataRow {
  const diagnosticId = statusData.diagnostic?.id ?? "";
  const diagnostic = diagnosticId ? lookups.diagnostics[diagnosticId] : undefined;
  const unit = diagnosticId ? lookups.units[diagnosticId] : undefined;

  return {
    id: statusData.id,
    diagnosticId,
    diagnosticName: diagnostic?.name ?? diagnosticId,
    value: toDisplayValue(statusData.data, unit),
    unitOfMeasure: unitFor(diagnosticId, lookups),
    dateTime: statusData.dateTime,
    source: (diagnosticId ? lookups.sources[diagnosticId] : "") ?? "",
  };
}

/**
 * The reading in the unit {@link unitFor} names. Converted here, once, so the grid, the checklist
 * and the fleet check all show the same number for the same reading.
 */
function toDisplayValue(value: number, unit: DisplayUnit | undefined): number {
  // Without a profile unit the diagnostic's own unit is shown, and that is the base unit.
  if (!unit?.name || typeof value !== "number" || typeof unit.factorFromBase !== "number") return value;

  return value * unit.factorFromBase + (unit.offsetFromBase ?? 0);
}

/**
 * The readings with their values in the unit {@link unitFor} names, for a page that works on raw
 * readings rather than rows. The motor data page labelled its figures with the profile's unit but
 * kept Geotab's base unit, so an odometer of 454.615,8 km read as 454.615.800 km.
 */
export function inDisplayUnits(statusData: StatusData[], lookups: LiveDataLookups): StatusData[] {
  return statusData.map(entry => {
    const diagnosticId = entry.diagnostic?.id;
    const unit = diagnosticId ? lookups.units[diagnosticId] : undefined;
    const data = toDisplayValue(entry.data, unit);
    return data === entry.data ? entry : { ...entry, data };
  });
}
